// Function to renew the token
export async function renewToken() {
    try {
        const oldToken = localStorage.getItem('token');

        // Make a POST request to the token renewal API endpoint
        const response = await fetch(process.env.REACT_APP_API_URI + '/api/refresh-token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ oldToken }),
        });

        // Check if the request was successful
        if (!response.ok) {
            throw new Error('Token renewal failed.');
        }

        // Extract the new token from the response
        const data = await response.json();
        const newToken = data.token;

        // Update the token in the local storage
        localStorage.setItem('token', newToken);

        // You may also update the user context with the new token if using context-based state management
        // userContext.setToken(newToken);

        console.log('Token renewed successfully.');


    } catch (error) {
        console.error(error);
    }
}

/**
 * Überprüft die Gültigkeit eines Passworts basierend auf bestimmten Kriterien.
 *
 * @param {string} password - Das zu überprüfende Passwort.
 * @returns {boolean} - Gibt true zurück, wenn das Passwort die Kriterien erfüllt, ansonsten false.
 */
export const isPasswordValid = (password) => {
    /**
     * Mindestlänge, die das Passwort haben muss.
     */
    const minLength = 12;

    /**
     * Überprüft, ob das Passwort einen Großbuchstaben enthält.
     */
    const hasUpperCase = /[A-Z]/.test(password);

    /**
     * Überprüft, ob das Passwort einen Kleinbuchstaben enthält.
     */
    const hasLowerCase = /[a-z]/.test(password);

    /**
     * Überprüft, ob das Passwort mindestens eine Zahl enthält.
     */
    const hasNumber = /[0-9]/.test(password);

    /**
     * Überprüft, ob das Passwort ein Sonderzeichen enthält.
     * Hier werden verschiedene Sonderzeichen durch die regulären Ausdrücke erkannt.
     */
    const hasSpecialChar = /[!@#$%^&*()_+\-={};':"|,.<>?]+/.test(password);

    /**
     * Gibt true zurück, wenn das Passwort die Kriterien erfüllt 
     * (Länge, Großbuchstabe, Kleinbuchstabe, Zahl, Sonderzeichen),
     * andernfalls wird false zurückgegeben.
     */
    return password.length >= minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
};

/**
 * Überprüft einzelne Passwort-Anforderungen und gibt den Status zurück.
 *
 * @param {string} password - Das zu überprüfende Passwort.
 * @returns {object} - Objekt mit dem Status jeder Anforderung.
 */
export const getPasswordRequirementStatus = (password) => {
    return {
        minLength: password.length >= 12,
        hasUpperCase: /[A-Z]/.test(password),
        hasLowerCase: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password),
        hasSpecialChar: /[!@#$%^&*()_+\-={};':"|,.<>?]+/.test(password)
    };
};

/**
 * Generiert eine detaillierte Fehlermeldung für Passwort-Anforderungen.
 *
 * @param {string} password - Das zu überprüfende Passwort.
 * @returns {string} - Fehlermeldung mit nicht erfüllten Anforderungen.
 */
export const getPasswordErrorMessage = (password) => {
    const requirements = getPasswordRequirementStatus(password);
    const errors = [];

    if (!requirements.minLength) {
        errors.push('mindestens 12 Zeichen');
    }
    if (!requirements.hasUpperCase) {
        errors.push('einen Großbuchstaben (A-Z)');
    }
    if (!requirements.hasLowerCase) {
        errors.push('einen Kleinbuchstaben (a-z)');
    }
    if (!requirements.hasNumber) {
        errors.push('eine Zahl (0-9)');
    }
    if (!requirements.hasSpecialChar) {
        errors.push('ein Sonderzeichen (!@#$%^&*()_+-={};\':|,.<>?)');
    }

    if (errors.length === 0) {
        return '';
    }

    return `Das Passwort muss enthalten: ${errors.join(', ')}`;
};

/**
 * Verarbeitet Lockout-Fehler und gibt angemessene deutsche Nachrichten zurück.
 *
 * @param {object} error - Der Fehler vom API-Server.
 * @returns {string} - Deutsche Fehlermeldung für Lockout.
 */
export const processLockoutError = (error) => {
    if (!error || typeof error !== 'object') {
        return 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es später erneut.';
    }

    // Prüfe auf verschiedene Lockout-Szenarien
    if (error.code === 'ACCOUNT_LOCKED') {
        if (error.lockout_until) {
            const lockoutUntil = new Date(error.lockout_until);
            const now = new Date();
            const diffMs = lockoutUntil - now;
            
            if (diffMs > 0) {
                const minutes = Math.ceil(diffMs / (1000 * 60));
                if (minutes > 60) {
                    const hours = Math.ceil(minutes / 60);
                    return `Ihr Konto ist für ${hours} Stunde${hours !== 1 ? 'n' : ''} gesperrt. Versuchen Sie es später erneut.`;
                } else {
                    return `Ihr Konto ist für ${minutes} Minute${minutes !== 1 ? 'n' : ''} gesperrt. Versuchen Sie es später erneut.`;
                }
            } else {
                return 'Ihr Konto war gesperrt, ist aber jetzt wieder verfügbar. Bitte versuchen Sie es erneut.';
            }
        } else {
            return 'Ihr Konto ist aufgrund mehrerer fehlgeschlagener Anmeldeversuche gesperrt. Bitte wenden Sie sich an den Administrator.';
        }
    }

    if (error.code === 'INVALID_CREDENTIALS') {
        const remainingAttempts = error.remaining_attempts;
        if (remainingAttempts !== undefined) {
            if (remainingAttempts <= 0) {
                return 'Ungültige Anmeldedaten. Ihr Konto wurde aufgrund zu vieler fehlgeschlagener Versuche gesperrt.';
            } else if (remainingAttempts <= 2) {
                return `Ungültige Anmeldedaten. ${remainingAttempts} Versuch${remainingAttempts !== 1 ? 'e' : ''} verbleib${remainingAttempts !== 1 ? 'en' : 't'} bis zur Kontosperrung.`;
            } else {
                return 'Ungültige Anmeldedaten. Bitte überprüfen Sie E-Mail-Adresse und Passwort.';
            }
        }
        return 'Ungültige Anmeldedaten. Bitte überprüfen Sie E-Mail-Adresse und Passwort.';
    }

    // Fallback für andere Fehlermeldungen
    return error.message || 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es später erneut.';
};

